import { useCallback, useState } from "react";
import { App, Button } from "antd";
import { defineMessages, useIntl } from "react-intl";
import { useInterval } from "react-use";
import { captureException } from "@sentry/nextjs";

const messages = defineMessages({
  title: {
    id: "new-version.title",
    defaultMessage: "New version is available",
  },
  message: {
    id: "new-version.message",
    defaultMessage:
      "A new version of the application is available. Please refresh page to update.",
  },
  button: {
    id: "new-version.button",
    defaultMessage: "Refresh",
  },
});

export function useCheckNewVersion({ skip }: { skip?: boolean } = {}) {
  const intl = useIntl();
  const { notification } = App.useApp();

  const [notificationShown, setNotificationShown] = useState(false);

  const openNotification = useCallback(() => {
    if (notificationShown) return;

    notification.info({
      title: intl.formatMessage(messages.title),
      description: intl.formatMessage(messages.message),
      duration: 0,
      closeIcon: <></>,
      actions: (
        <Button type="primary" onClick={() => window.location.reload()}>
          {intl.formatMessage(messages.button)}
        </Button>
      ),
    });

    setNotificationShown(true);
  }, [intl, notification, notificationShown]);

  const disableFetch = skip || !process.env.ARAGRO_ENV || notificationShown;

  useInterval(
    async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const resp = await fetch("/api/version", {
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!resp.ok) {
          throw new Error(`HTTP error! status: ${resp.status}`);
        }

        const { clientVersion } = await resp.json();
        if (!clientVersion) return;

        if (clientVersion !== process.env.CLIENT_VERSION) {
          openNotification();
        }
      } catch (error) {
        if (error instanceof Error) {
          if (
            error.name === "AbortError" ||
            error.message.includes("Failed to fetch") ||
            error.message.includes("Load failed") ||
            error.message.includes("NetworkError")
          ) {
            return; // Silently ignore network/abort errors across browsers
          }

          captureException(error, {
            tags: { component: "version-check" },
            level: "warning",
          });
        } else {
          captureException(new Error(String(error)), {
            tags: { component: "version-check" },
            level: "warning",
          });
        }
      }
    },
    disableFetch ? null : 300000
  );
}
